import React from 'react';
import { Text, VStack, Box, Icon, Link, Flex } from '@chakra-ui/react';
import { CheckIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import ApprovedContentPreview from './ApprovedContentPreview';
import { isVideoUrl } from '../../../utils/fileUtils';
import { ff } from '../../../theme';
import ModalShell from '../../../components/ui/ModalShell';
import { StatusPill } from './submissionSheetParts';

export interface CompletedPostLink {
  label: string;
  url: string;
}

interface CompletedModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  body: string;
  /** Single approved asset URL. */
  contentUrl?: string;
  /** Multiple approved asset URLs — renders a horizontally scrollable row. */
  contentUrls?: string[];
  /** The influencer's live post, linked out to when known. */
  postLink?: string;
  /** Multiple live post links — one per platform when content was cross-posted. */
  postLinks?: CompletedPostLink[];
}

const ContentPreview: React.FC<{ url: string; index?: number; fillWidth?: boolean }> = ({
  url,
  index,
  fillWidth = false,
}) => {
  const isVideo = isVideoUrl(url);

  return (
    <Box
      position="relative"
      bg={ff.ink}
      borderRadius={fillWidth ? '18px' : '14px'}
      overflow="hidden"
      flexShrink={0}
      w={fillWidth ? '100%' : '72%'}
      maxW={fillWidth ? undefined : '280px'}
      sx={{ scrollSnapAlign: 'start' }}
    >
      {isVideo ? (
        <video
          controls
          src={url}
          style={{
            width: '100%',
            maxHeight: '280px',
            display: 'block',
            objectFit: 'contain',
          }}
          preload="metadata"
        />
      ) : (
        <img
          src={url}
          alt={index !== undefined ? `Approved content ${index + 1}` : 'Approved content'}
          style={{
            width: '100%',
            maxHeight: '280px',
            display: 'block',
            objectFit: 'contain',
          }}
        />
      )}
      {index !== undefined && (
        <Flex
          position="absolute"
          top="8px"
          left="8px"
          minW="26px"
          h="26px"
          px="6px"
          borderRadius="full"
          bg={ff.ink}
          align="center"
          justify="center"
        >
          <Text fontSize="13px" fontWeight={600} color={ff.lime} lineHeight={1}>
            {index + 1}
          </Text>
        </Flex>
      )}
    </Box>
  );
};

const CompletedModal: React.FC<CompletedModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  body,
  contentUrl,
  contentUrls,
  postLink,
  postLinks,
}) => {
  const urls = contentUrls?.length ? contentUrls : contentUrl ? [contentUrl] : [];
  const isMultiContent = urls.length > 1;
  const livePostLinks = postLinks?.length
    ? postLinks
    : postLink
      ? [{ label: 'Live post', url: postLink }]
      : [];
  const hasArchive = urls.length > 0 || livePostLinks.length > 0;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      primary={{ label: 'Done', onClick: onClose, tone: 'lime' }}
>
      <VStack spacing="16px" align="stretch">
        <Box
          bg={ff.ink}
          color={ff.lime}
          borderRadius="full"
          w="48px"
          h="48px"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CheckIcon w={5} h={5} />
        </Box>

        <Text as="h2" m={0} textStyle="display" fontSize="27px" lineHeight={1.12} letterSpacing="0">
          {title}
        </Text>

        <Text mt="-8px" fontSize="14px" fontWeight={600} color="#55555A">
          {subtitle}
        </Text>

        {!hasArchive && (
          <Text fontSize="14px" lineHeight={1.4} color={ff.body}>
            {body}
          </Text>
        )}

        {urls.length === 1 && (
          <Box w="100%">
            <ApprovedContentPreview
              url={urls[0]}
              label="Your content"
              fileNameBase={subtitle}
              instruction={null}
            />
          </Box>
        )}

        {isMultiContent && (
          <Box w="100%">
            <Flex align="center" justify="space-between" mb="10px" gap={3}>
              <Text fontSize="12px" fontWeight={600} color="#55555A" textTransform="uppercase" letterSpacing="0.08em">
                Your content · {urls.length} items
              </Text>
              <StatusPill tone="green">Approved</StatusPill>
            </Flex>
            <Flex
              overflowX="auto"
              gap="10px"
              pb={1}
              sx={{
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              {urls.map((url, index) => (
                <ContentPreview key={`${url}-${index}`} url={url} index={index} />
              ))}
            </Flex>
          </Box>
        )}

        {livePostLinks.length > 0 && (
          <VStack spacing={2} w="100%" align="stretch">
            {livePostLinks.map(({ label, url }) => (
              <Link
                key={`${label}-${url}`}
                href={url}
                isExternal
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap="12px"
                minH="44px"
                px="14px"
                borderRadius="14px"
                border={`1px solid ${ff.border}`}
                fontSize="14px"
                color={ff.ink}
                fontWeight={600}
                maxW="100%"
                _hover={{ textDecoration: 'none', bg: ff.grey }}
              >
                <Text as="span" isTruncated minW={0}>
                  {livePostLinks.length > 1 ? `View ${label}` : 'View live post'}
                </Text>
                <Icon as={ExternalLinkIcon} boxSize={3.5} flexShrink={0} />
              </Link>
            ))}
          </VStack>
        )}
      </VStack>
    </ModalShell>
  );
};

export default CompletedModal;
